/**
 * Hook for managing answer state
 * Extracted from DeviceSpecifications.tsx
 */

import { useState, useCallback } from 'react';
import type { DynamicQuestion } from '../types';

/**
 * Hook to manage answers state
 * 
 * @param questions - Questions array (for preserving answers when questions change)
 * @returns Object with answers state and update function
 */
export function useAnswers(questions: DynamicQuestion[]) {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Preserve existing answers when questions change
  const preserveAnswers = useCallback((newQuestions: DynamicQuestion[]) => {
    setAnswers((prev: Record<string, string>) => {
      const next: Record<string, string> = {};
      for (const q of newQuestions) {
        const key = q.questionKey;
        if (prev[key]) {
          next[key] = prev[key];
        }
      }
      return next;
    });
  }, []);

  // Update answer for a specific question
  const updateAnswer = useCallback((questionKey: string, optionKey: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: optionKey,
    }));
  }, []);

  // Clear all answers
  const clearAnswers = useCallback(() => {
    setAnswers({});
  }, []);

  return {
    answers,
    setAnswers,
    preserveAnswers,
    updateAnswer,
    clearAnswers,
  };
}

